import { Injectable, inject, Injector, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { OAuthService, AuthConfig } from 'angular-oauth2-oidc';
import { BehaviorSubject, Observable, filter } from 'rxjs';
import { environment } from '../../environments/environment';
import { UserService } from '../../services/user.service';

export interface UserProfile {
  sub: string;
  email?: string;
  name?: string;
  preferred_username?: string;
  given_name?: string;
  family_name?: string;
  email_verified?: boolean;
  roles?: string[];
}

@Injectable({
  providedIn: 'root'
})
export class KeycloakAuthService {
  private platformId = inject(PLATFORM_ID);
  private oauthService = inject(OAuthService);
  private router = inject(Router);
  private injector = inject(Injector);

  private isAuthenticatedSubject = new BehaviorSubject<boolean>(false);
  private userProfileSubject = new BehaviorSubject<UserProfile | null>(null);
  private isInitializedSubject = new BehaviorSubject<boolean>(false);

  isAuthenticated$: Observable<boolean> = this.isAuthenticatedSubject.asObservable();
  userProfile$: Observable<UserProfile | null> = this.userProfileSubject.asObservable();
  isInitialized$: Observable<boolean> = this.isInitializedSubject.asObservable();

  private getAuthConfig(): AuthConfig {
    // document.baseURI reads the <base href> tag (e.g. /configurator/) — reliable regardless of trailing slash in the URL
    const redirectUri = typeof document !== 'undefined' ? document.baseURI : (typeof window !== 'undefined' ? window.location.origin + '/' : '/');
    return {
      issuer: environment.keycloak.issuer,
      clientId: environment.keycloak.clientId,
      redirectUri: redirectUri,
      postLogoutRedirectUri: redirectUri,
      scope: environment.keycloak.scope,
      responseType: environment.keycloak.responseType,
      showDebugInformation: false,
      requireHttps: environment.keycloak.requireHttps ?? true,
      // Use refresh tokens instead of silent refresh iframe
      useSilentRefresh: false,
      timeoutFactor: 0.75,
      sessionChecksEnabled: false,
      clearHashAfterLogin: true,
      nonceStateSeparator: 'semicolon'
    };
  }

  async init(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      this.oauthService.configure(this.getAuthConfig());

      // Subscribe to token events
      this.oauthService.events
        .pipe(filter(e => e.type === 'token_received'))
        .subscribe(async () => await this.handleTokenReceived());

      this.oauthService.events
        .pipe(filter(e => e.type === 'session_terminated' || e.type === 'token_refresh_error'))
        .subscribe(() => {
          this.handleSessionTerminated();
        });

      // Load discovery document and try login
      await this.oauthService.loadDiscoveryDocumentAndTryLogin();

      // Clean up URL after login (remove state, code, session_state params)
      this.cleanUrlAfterLogin();

      // Setup automatic token refresh using refresh tokens
      // This will refresh the access token before it expires using the refresh token
      if (this.oauthService.hasValidAccessToken()) {
        this.setupTokenRefresh();
      }

      // Check if user is authenticated
      const isAuthenticated = this.oauthService.hasValidAccessToken();
      this.isAuthenticatedSubject.next(isAuthenticated);

      if (isAuthenticated) {
        await this.loadUserProfile();
        await this.loadUserPermissions();
      }

      this.isInitializedSubject.next(true);
      return isAuthenticated;
    } catch (error) {
      console.error('Error initializing Keycloak auth:', error);
      this.isInitializedSubject.next(true);
      return false;
    }
  }

  private setupTokenRefresh(): void {
    const expiresAt = this.oauthService.getAccessTokenExpiration();
    const now = Date.now();
    const expiresIn = expiresAt - now;
    const refreshIn = expiresIn * 0.75;

    if (refreshIn > 0) {
      setTimeout(() => {
        this.doTokenRefresh();
      }, refreshIn);
    }
  }

  private async doTokenRefresh(): Promise<void> {
    try {
      await this.oauthService.refreshToken();
      this.setupTokenRefresh();
    } catch (error) {
      console.error('Failed to refresh token:', error);
      this.handleSessionTerminated();
    }
  }

  login(returnUrl?: string): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (returnUrl) {
      sessionStorage.setItem('returnUrl', returnUrl);
    }

    const additionalParams: Record<string, string> = {};
    const skipIdpHint = sessionStorage.getItem('skipIdpHint');
    if (environment.keycloak.idpHint && !skipIdpHint) {
      additionalParams['kc_idp_hint'] = environment.keycloak.idpHint;
    }
    sessionStorage.removeItem('skipIdpHint');
    this.oauthService.initCodeFlow('', additionalParams);
  }

  logout(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    sessionStorage.setItem('skipIdpHint', 'true');

    // Get the id token before clearing
    const idToken = this.oauthService.getIdToken();

    // Clear all local state and tenant information
    this.isAuthenticatedSubject.next(false);
    this.userProfileSubject.next(null);
    this.isInitializedSubject.next(false);

    // Clear all tenant-related data from localStorage to unauthenticate from any tenant
    localStorage.removeItem('tenantId');
    localStorage.removeItem('userPermissions');

    if (idToken) {
      // Logout from Keycloak with id_token_hint
      this.oauthService.logOut({
        idTokenHint: idToken
      });
    } else {
      // No token, just clear storage and redirect home
      window.location.href = document.baseURI;
    }
  }

  get isAuthenticated(): boolean {
    return this.oauthService.hasValidAccessToken();
  }

  get accessToken(): string | null {
    return this.oauthService.getAccessToken();
  }

  get idToken(): string | null {
    return this.oauthService.getIdToken();
  }

  get identityClaims(): UserProfile | null {
    const claims = this.oauthService.getIdentityClaims();
    return claims as UserProfile | null;
  }

  get tokenExpiration(): Date | null {
    const expiration = this.oauthService.getAccessTokenExpiration();
    return expiration ? new Date(expiration) : null;
  }

  async refreshToken(): Promise<boolean> {
    if (!isPlatformBrowser(this.platformId)) {
      return false;
    }

    try {
      await this.oauthService.refreshToken();
      return true;
    } catch (error) {
      console.error('Error refreshing token:', error);
      return false;
    }
  }

  private async loadUserProfile(): Promise<void> {
    try {
      const userInfo = await this.oauthService.loadUserProfile();
      const claims = this.oauthService.getIdentityClaims() as any;

      const profile: UserProfile = {
        sub: claims?.sub || '',
        email: claims?.email,
        name: claims?.name,
        preferred_username: claims?.preferred_username,
        given_name: claims?.given_name,
        family_name: claims?.family_name,
        email_verified: claims?.email_verified,
        roles: this.extractRoles(claims)
      };

      this.userProfileSubject.next(profile);
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  /**
   * Fetches user permissions from the backend and stores them in localStorage.
   * Uses Injector to lazily resolve UserService to avoid circular dependency.
   */
  private async loadUserPermissions(): Promise<void> {
    try {
      const claims = this.oauthService.getIdentityClaims() as any;
      const userId = claims?.sub;
      if (!userId) {
        console.warn('Cannot load permissions: no user ID in token claims');
        return;
      }

      const userService = this.injector.get(UserService);
      const permissions = await userService.getPermissionsOfUser(userId);
      localStorage.setItem('userPermissions', JSON.stringify(permissions));
    } catch (error) {
      console.error('Error loading user permissions:', error);
    }
  }

  private extractRoles(claims: any): string[] {
    // Keycloak stores roles in realm_access.roles and resource_access
    const roles: string[] = [];

    if (claims?.realm_access?.roles) {
      roles.push(...claims.realm_access.roles);
    }

    // Extract client-specific roles
    if (claims?.resource_access) {
      Object.values(claims.resource_access).forEach((resource: any) => {
        if (resource?.roles) {
          roles.push(...resource.roles);
        }
      });
    }

    return [...new Set(roles)]; // Remove duplicates
  }

  private async handleTokenReceived(): Promise<void> {
    const wasAuthenticated = this.isAuthenticatedSubject.value;
    this.isAuthenticatedSubject.next(true);

    // Only fetch profile and permissions on initial login, not on every token refresh
    if (!wasAuthenticated) {
      await this.loadUserProfile();
      await this.loadUserPermissions();

      // Handle return URL after login
      const returnUrl = sessionStorage.getItem('returnUrl');
      if (returnUrl) {
        sessionStorage.removeItem('returnUrl');
        this.router.navigateByUrl(returnUrl);
      }
    }
  }

  private cleanUrlAfterLogin(): void {
    // Remove OAuth query parameters from URL
    const url = new URL(window.location.href);
    const paramsToRemove = ['code', 'state', 'session_state', 'iss'];
    let hasParams = false;

    paramsToRemove.forEach(param => {
      if (url.searchParams.has(param)) {
        url.searchParams.delete(param);
        hasParams = true;
      }
    });

    if (hasParams) {
      // Navigate to the clean path using Angular router to avoid stale query params
      const cleanPath = url.pathname + url.search;
      this.router.navigateByUrl(cleanPath, { replaceUrl: true });
    }
  }

  private handleSessionTerminated(): void {
    this.isAuthenticatedSubject.next(false);
    this.userProfileSubject.next(null);
    localStorage.removeItem('tenantId');
    localStorage.removeItem('userPermissions');
  }

  hasRole(role: string): boolean {
    const profile = this.userProfileSubject.value;
    return profile?.roles?.includes(role) ?? false;
  }

  hasAnyRole(roles: string[]): boolean {
    return roles.some(role => this.hasRole(role));
  }
}
